import { useState } from "react";

import type { InboundSubmission, OrganisationRecord } from "../api/hooks";
import {
  useClearInbound,
  useCreateOrganisationRecord,
  useOrganisationRecords,
  useResolveInbound,
} from "../api/hooks";
import { SlideOver } from "../components/SlideOver";
import { useDebouncedValue } from "../lib/useDebouncedValue";

import styles from "./-InboundOrganisationPanel.module.scss";

// The name the enquirer typed, which is a starting point for the search and
// nothing more. It is free text on a public form: it is never matched against
// anything, only offered back so the operator does not retype it.
function typedName(submission: InboundSubmission): string {
  return submission.organisation?.trim() ?? "";
}

export function InboundOrganisationPanel({
  submission,
  onClose,
}: {
  submission: InboundSubmission;
  onClose: () => void;
}) {
  const [search, setSearch] = useState(typedName(submission));
  const [message, setMessage] = useState("");

  const searchTerm = useDebouncedValue(search.trim(), 250);
  const { data, isPending, isError, isFetching, hasNextPage, fetchNextPage } =
    useOrganisationRecords({
      search: searchTerm === "" ? undefined : searchTerm,
    });

  const resolve = useResolveInbound();
  const clear = useClearInbound();
  const create = useCreateOrganisationRecord();

  const records = data?.pages.flatMap((page) => page.items) ?? [];
  const total = data?.pages[0]?.count ?? 0;
  const busy = resolve.isPending || clear.isPending || create.isPending;

  function link(record: OrganisationRecord) {
    setMessage("");

    resolve.mutate(
      { submission, organisationId: record.id },
      {
        onSuccess: onClose,
        onError: () => setMessage("That could not be linked. Try again."),
      },
    );
  }

  // Creating and linking is two calls, and the second can fail on its own. The
  // record exists either way by then, so saying "nothing happened" would send
  // the operator to create it a second time.
  function createAndLink() {
    setMessage("");

    create.mutate(
      {
        name: search.trim(),
        kind: "",
        county: "",
        hectares: null,
        website: "",
        charityNumber: "",
        companyNumber: "",
        organisationId: null,
      },
      {
        onSuccess: (record) =>
          resolve.mutate(
            { submission, organisationId: record.id },
            {
              onSuccess: onClose,
              onError: () =>
                setMessage(
                  `${record.name} was added, but linking it to this enquiry failed. It is in the organisations list - try linking it again.`,
                ),
            },
          ),
        onError: () => setMessage("That could not be added. Try again."),
      },
    );
  }

  function unlink() {
    setMessage("");

    clear.mutate(submission, {
      onSuccess: onClose,
      onError: () => setMessage("That could not be unlinked. Try again."),
    });
  }

  // Only when the box holds something no record already answers to. Offering
  // "create" beside an exact match is how a second record for the same body
  // gets made, and nothing merges them afterwards.
  const exactMatch = records.some(
    (record) => record.name.toLowerCase() === search.trim().toLowerCase(),
  );

  // The results have to be the results for what is in the box. Between a
  // keystroke and the search that follows it, the previous term's records are
  // still on screen - kept deliberately, so the list does not blink - and they
  // cannot answer whether this term already exists. Offering "add" against
  // them is how the duplicate this guard exists to prevent gets made.
  const searchSettled = searchTerm === search.trim() && !isFetching;

  // And the whole result set has to be on screen, or the record that already
  // answers to the name may be on a page nobody has loaded: exactMatch can only
  // speak for the rows it has. "Showing 20 of 46" beside an offer to add the
  // 21st is how the duplicate gets made anyway.
  const canCreate =
    search.trim() !== "" &&
    searchSettled &&
    !hasNextPage &&
    !exactMatch &&
    !isError;

  return (
    <SlideOver
      title={`${submission.firstName} ${submission.lastName}`}
      // busy is the whole refusal: a close mid-write drops the callbacks that
      // finish it, so an add would leave a record made and nothing linked, with
      // nothing on screen having said so. SlideOver holds every close path,
      // which is where the guard belongs - a second one here would hide the
      // component's own from every test.
      onClose={onClose}
      busy={busy}
    >
      <p className={styles.lead}>
        Which organisation is this about? Nothing is matched automatically - the
        name on a public form is whatever the sender typed.
      </p>

      {submission.resolvedOrganisation && (
        <div className={styles.current}>
          <span className={styles.currentName}>
            {submission.resolvedOrganisation.name}
          </span>
          <button
            type="button"
            className={styles.unlink}
            disabled={busy}
            onClick={unlink}
          >
            Unlink
          </button>
        </div>
      )}

      {message !== "" && (
        <div className={styles.error} role="alert">
          {message}
        </div>
      )}

      <input
        type="search"
        className={styles.search}
        aria-label="Search organisations"
        placeholder="Search organisations"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      {isPending && (
        <div className={styles.state} role="status">
          Loading…
        </div>
      )}

      {isError && (
        <div className={styles.state} role="alert">
          The organisations could not be loaded.
        </div>
      )}

      {!isPending && !isError && records.length === 0 && (
        <div className={styles.state} role="status">
          No organisation matches that.
        </div>
      )}

      <ul className={styles.records}>
        {records.map((record) => (
          <li key={record.id}>
            <button
              type="button"
              className={styles.record}
              disabled={busy}
              onClick={() => link(record)}
            >
              <span className={styles.recordName}>{record.name}</span>
              <span className={styles.recordMeta}>
                {[record.kind, record.county, record.websiteDomain]
                  .filter((part) => part !== "")
                  .join(" · ")}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {/* Said rather than implied: a search matching more than a page looks
          exactly like one matching all of them, and a record past the end is
          invisible to the operator and to the exact-match guard alike. */}
      {/* Gated on the query's own cursor, not on the count: the count and the
          page come from two statements, so a row inserted between them would
          otherwise leave a button that says "load more" and loads nothing. */}
      {hasNextPage && (
        <button
          type="button"
          className={styles.more}
          disabled={isFetching}
          onClick={() => fetchNextPage()}
        >
          {isFetching
            ? "Loading…"
            : `Showing ${records.length} of ${total} - load more`}
        </button>
      )}

      {canCreate && (
        <button
          type="button"
          className={styles.create}
          disabled={busy}
          onClick={createAndLink}
        >
          Add “{search.trim()}” as a new organisation
        </button>
      )}
    </SlideOver>
  );
}
